import { PackageSortConfig, PackageSortField } from '../interfaces'

export const PACKAGE_SORT_FIELDS: PackageSortField[] = [
  'quantity',
  'price',
  'label',
  'name',
]

/**
 * Resolved sort, in the shape `sortByKey` takes. `null` means "leave the
 * catalog order alone", which covers both opting out and misconfiguration.
 */
export interface IResolvedPackageSort {
  field: PackageSortField
  reverse: boolean
}

/**
 * These read `unknown` rather than the declared types on purpose: the config is
 * remote JSON cast to `IConfig`, so at runtime these values can be anything.
 */
const resolveField = (field: unknown): PackageSortField | undefined => {
  if (field === undefined || field === null || field === '') {
    return 'quantity'
  }
  return PACKAGE_SORT_FIELDS.includes(field as PackageSortField)
    ? (field as PackageSortField)
    : undefined
}

const resolveDirection = (direction: unknown): 'asc' | 'desc' | undefined => {
  if (direction === undefined || direction === null || direction === '') {
    return 'asc'
  }
  if (typeof direction !== 'string') {
    return undefined
  }
  const normalized = direction.trim().toLowerCase()
  return normalized === 'asc' || normalized === 'desc' ? normalized : undefined
}

/**
 * True only when a storefront asked for a fixed package order and the rest of
 * the config cannot deliver one. Opting out is not a misconfiguration, so a
 * disabled or absent block stays quiet.
 */
export const isPackageSortMisconfigured = (
  sort: PackageSortConfig | undefined,
): boolean => {
  if (!sort) {
    return false
  }
  if (sort.enabled !== undefined && typeof sort.enabled !== 'boolean') {
    return true
  }
  if (sort.enabled !== true) {
    return false
  }
  return (
    resolveField(sort.field) === undefined ||
    resolveDirection(sort.direction) === undefined
  )
}

/**
 * The sort to apply, or `null` to keep the catalog order. Requires `enabled`
 * to be exactly `true`: the config is remote JSON, so a stringified `"false"`
 * is truthy, and a storefront that opted out must not be opted back in by a
 * quoting accident.
 */
export const resolvePackageSort = (
  sort: PackageSortConfig | undefined,
): IResolvedPackageSort | null => {
  if (sort?.enabled !== true) {
    return null
  }

  const field = resolveField(sort.field)
  const direction = resolveDirection(sort.direction)

  if (!field || !direction) {
    return null
  }

  return { field, reverse: direction === 'desc' }
}
